import type { ValidationResult } from "@react-types/shared";
import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import {
  type CSSProperties,
  type ForwardedRef,
  forwardRef,
  type ReactElement,
  type ReactNode,
  useRef,
  useState,
} from "react";
import type { AriaSearchFieldProps } from "react-aria";
import { mergeProps, useFocus, useSearchField, VisuallyHidden } from "react-aria";
import { useSearchFieldState } from "react-stately";

import { useDOMRef } from "../../hooks.ts";
import formStyle from "../../styles/form.module.css";
import { Badge } from "../Badge/Badge.tsx";
import { Description } from "../Description/Description.tsx";
import { Icon } from "../Icon/Icon.tsx";
import { SymbolIcon } from "../Icon/SymbolIcon.tsx";
import { Label } from "../Label/Label.tsx";
import { LoadingSpinner } from "../ProgressSpinner/LoadingSpinner.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { Spacer } from "../Stack/Spacer.tsx";
import { VStack } from "../Stack/VStack.tsx";
import style from "../TextField/TextField.module.css";
import { TriggerButton } from "../TriggerButton/TriggerButton.tsx";

export interface SearchFieldProps extends AriaSearchFieldProps {
  width?: number | string | undefined;
  isLoading?: boolean;
  icon?: ReactElement | MaterialSymbol;
  style?: CSSProperties;
  className?: string;
}

const SearchFieldRender = (props: SearchFieldProps, ref: ForwardedRef<HTMLInputElement>): ReactNode => {
  const state = useSearchFieldState(props);
  const inputRef = useDOMRef<HTMLInputElement>(ref);
  const clearRef = useRef<HTMLButtonElement>(null);
  const { labelProps, inputProps, clearButtonProps, descriptionProps, errorMessageProps } = useSearchField(
    props,
    state,
    inputRef,
  );

  const [isFocused, onFocusChange] = useState(false);
  const { focusProps } = useFocus({ onFocusChange });

  return (
    <VStack
      spacing={2}
      style={{ ...props.style, width: props.width ?? props.style?.width ?? 170 }}
      data-testid={`${props.name ? `${props.name}__` : ""}search-field`}
    >
      {!!props.label && (
        <HStack className={formStyle.input_layout_column1} style={{ alignItems: "baseline" }}>
          <Label {...labelProps}>{props.label}</Label>
          <Spacer />
          {!!props.isReadOnly && <Badge intent="light" large inline label="read only" style={{ flex: "0 0 auto" }} />}
          {!props.isRequired && !props.isReadOnly && (
            <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />
          )}
        </HStack>
      )}
      <Description
        {...descriptionProps}
        data-testid="search-field-description"
        className={clsx(formStyle.input_layout_column1, {
          [formStyle.description_with_tag]: props.label != undefined && (props.isReadOnly || !props.isRequired),
        })}
      >
        {props.description}
      </Description>
      <HStack
        spacing={3}
        className={clsx(style.input_group, {
          [style.disabled]: !!props.isDisabled,
          [style.focus]: isFocused,
          [style.error]: props.isInvalid,
        })}
      >
        {!props.label && (
          <VisuallyHidden>
            <label {...labelProps}>{props.placeholder}</label>
          </VisuallyHidden>
        )}
        <Icon icon={props.icon ?? "search"} className={style.icon} />
        <input {...mergeProps(inputProps, focusProps)} ref={inputRef} className={style.input} />
        <div className={style.action}>
          {state.value !== "" && !props.isLoading && (
            <TriggerButton
              {...clearButtonProps}
              ref={clearRef}
              icon={<SymbolIcon icon="cancel" filled size={16} style={{ color: "var(--form-placeholder)" }} />}
              label=""
              data-testid="clear-search-field"
              data-e2e-clear
              aria-label={"Clear"}
            />
          )}
          {!!props.isLoading && <LoadingSpinner size={20} />}
        </div>
      </HStack>
      {props.errorMessage != undefined && !!props.isInvalid && !props.isDisabled && (
        <div role="alert" {...errorMessageProps} className={formStyle.error_message} data-testid="number-field-error">
          {typeof props.errorMessage === "function" ? props.errorMessage({} as ValidationResult) : props.errorMessage}
        </div>
      )}
    </VStack>
  );
};

export const SearchField = forwardRef(SearchFieldRender);
